/**
 * What the local platform host says it is carrying.
 *
 * The stable platform serves one static build and may have zero, one or two domain services behind
 * it. The browser cannot infer that from its own URL: the build is identical either way. So the host
 * states it, in a marker it injects into the page beside the release marker, and the app reads that
 * marker before its first render.
 *
 * WHAT THIS IS NOT. It is not authorization, and nothing may treat it as one. It says only that the
 * operator declared a management origin when starting the platform, so a menu may offer destinations
 * that can answer. Every request is still decided by the host's own identity, scope, session, CSRF,
 * Host and Origin checks, and by this platform refusing the whole `/management/` family when no
 * origin was declared. A forged marker changes which links are drawn and nothing else.
 */
export interface PlatformServices {
  readonly management: boolean;
}

function isLoopbackHostname(hostname: string): boolean {
  return ['localhost', '127.0.0.1', '[::1]'].includes(hostname);
}

/** The injected marker, or null when this page was not served by the local platform host. */
export function platformServices(
  doc: Document | null = typeof document === 'undefined' ? null : document,
): PlatformServices | null {
  const raw = doc?.getElementById('platform-services')?.textContent;
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as { management?: unknown } | null;
    return { management: value?.management === true };
  } catch {
    // A marker this app cannot parse is treated as absent rather than as an error: the page it
    // would have enabled is one that writes to a database, and "cannot tell" must not mean "yes".
    return null;
  }
}

/**
 * Whether this page may offer the local synthetic business destinations.
 *
 * THREE CONDITIONS, ALL REQUIRED, and each one closes a different way the answer could be wrong:
 *
 *   DEV       — the same gate the synthetic portals already use, so a production build can never
 *               offer them however the page was reached;
 *   loopback  — the destinations talk to the platform's own origin, and only the local platform
 *               serves the synthetic business;
 *   management — the host must have been started with an explicit management origin.
 *
 * A false answer hides links. It grants nothing, and it revokes nothing.
 */
export function syntheticBusinessEnabled(
  dev = import.meta.env.DEV,
  location: Location | null = typeof window === 'undefined' ? null : window.location,
  services = platformServices(),
): boolean {
  return Boolean(
    dev && location && isLoopbackHostname(location.hostname) && services?.management === true,
  );
}
